/* ==========================================================================
   <fe-stacked-hero> — a pinned stage whose panels swap on scroll.

   The stage sticks to the top of the viewport for `panels x step` of scroll.
   Scroll position picks the panel; the panel picks which flywheel hex lights
   up. Nothing moves except the copy and, once, the flywheel itself.

   Falls back to a plain vertical stack when JS is idle, motion is reduced,
   or the viewport is narrow — the same markup, no pinning.

   Over the honeycomb ground the stage also fades from light to brand teal.
   The panels advance one per flick, so a fade paced by the page scroll alone
   (the export's 1.85 screens) is over in two flicks and its steps are never
   seen. By default (data-fade="all") the section paces it by the panels
   instead: the fade starts the moment the stage sticks to the top of the
   screen and lands fully teal as the final panel arrives, so every panel
   shows the next step of the brand transition. trackFade works out how far
   along the run the page is and hands that to <fe-honeycomb>. data-fade can
   narrow it to the last few panels, or "window" hands the pacing back to the
   honeycomb's own page-scroll range as exported. The blend belongs to the
   pinned stage either way: in the flow layout — phones, reduced motion — the
   panels are a plain stack, and the honeycomb stays on its light look.

   The theme's header slides back in whenever the page is scrolled up. On the
   pinned stage every step back through the panels is a scroll up, and the
   header dropping over the stage each time reads as a glitch — so while the
   stage is held, a scroll up asks the header to stay put (data-hold-header).
   Everywhere else the header behaves as it always did.

   data-advance="snap"  one wheel flick, arrow key or rail click moves exactly
                        one panel; the page takes the scroll back at either end
   data-advance="free"  the panel follows scroll position proportionally
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  /* 768px and under — a tablet held upright included — is the mobile stack;
     the stage pins from 769px up. Matches the stylesheet's own boundary. */
  const WIDE = matchMedia('(min-width: 769px)');

  class FeStackedHero extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.scroller = this.querySelector('.fe-stack__scroll');
      this.stage = this.querySelector('.fe-stack__stage');
      this.deck = this.querySelector('.fe-stack__deck');
      this.lattices = [...this.querySelectorAll('fe-hex-lattice')];
      this.flier = this.querySelector('.fe-stack__flier');
      this.landing = this.querySelector('.fe-mark--slot');
      this.panels = [...this.querySelectorAll('.fe-stack__panel')];
      this.hexes = [...this.querySelectorAll('.fe-flywheel__hex')];
      this.railBtns = [...this.querySelectorAll('.fe-stack__rail-btn')];
      this.honeycomb = this.querySelector('fe-honeycomb');

      if (!this.panels.length) return;

      this.index = -1;
      this.pinned = false;
      this.visible = true;
      this.queued = false;

      this.lock = false;
      this.lockTimer = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onKey = this.onKey.bind(this);
      this.evaluate = this.evaluate.bind(this);
      this.holdHeader = this.holdHeader.bind(this);

      /* Dawn's sticky header listens for preventHeaderReveal and, for as long
         as scroll events keep arriving, hides instead of revealing. It decides
         on each scroll event in the bubble phase, so the request has to reach
         it in the capture phase of the same event — hence its own listener,
         ahead of the rAF-throttled one below. */
      /* When the page has a ground of its own, the closing panel's is that
         ground — the stage tells it to go teal as the panel lands. Painting a
         second, opaque one on top would swap the texture for a flat colour
         and show the join where the stage ends, which is the one seam the
         single field exists to remove. */
      if (document.querySelector('fe-page-field')) this.setAttribute('data-field-backed', '');

      this.header = document.querySelector('sticky-header');
      this.lastY = scrollY;
      if (this.header && this.dataset.holdHeader !== 'false') {
        addEventListener('scroll', this.holdHeader, { capture: true, passive: true });
      }

      this.railBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(Number(btn.dataset.goto)));
      });

      this.io = new IntersectionObserver(
        (entries) => {
          this.visible = entries[entries.length - 1].isIntersecting;
          /* off screen, the stage has no say in what the page's ground is
             doing — whatever it last asked for is released here */
          if (!this.visible) this.tellField(null);
          else if (this.stage) this.tellField(this.stage.dataset.tone || null);
        },
        { rootMargin: '10% 0px' }
      );
      this.io.observe(this);

      addEventListener('resize', this.onResize, { passive: true });
      WIDE.addEventListener('change', this.evaluate);
      REDUCED.addEventListener('change', this.evaluate);

      this.initSwipe();
      this.evaluate();

      /* The fade is scroll-driven in both layouts, so the listener is not
         tied to pinning. Its element may be defined after this one — both
         scripts are deferred — so the first hand-off waits for it. */
      if (this.honeycomb) {
        /* the stage's two-treatment pieces — pills, the mark — follow the
           honeycomb's own reading of its ground, however the progress is fed */
        this.addEventListener('fe-honeycomb:progress', (event) => {
          this.stage.dataset.shade = event.detail.shade;
        });
        /* its element may be defined after this one — both scripts are
           deferred — so the first hand-off waits for it */
        customElements.whenDefined('fe-honeycomb').then(() => this.trackFade());
      }

      /* type metrics settle after the webfont lands */
      document.fonts?.ready.then(() => {
        this.measure();
        this.swipeHeight = '';
        this.syncSwipe();
      });

      /* theme editor: jump to whichever panel the merchant is editing */
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest?.('.fe-stack__panel');
        if (panel) this.goTo(this.panels.indexOf(panel), 'auto');
      });
    }

    disconnectedCallback() {
      removeEventListener('resize', this.onResize);
      removeEventListener('scroll', this.onScroll);
      removeEventListener('scroll', this.holdHeader, { capture: true });
      removeEventListener('wheel', this.onWheel);
      removeEventListener('keydown', this.onKey);
      WIDE.removeEventListener('change', this.evaluate);
      REDUCED.removeEventListener('change', this.evaluate);
      this.io?.disconnect();
      clearTimeout(this.resizeTimer);
      clearTimeout(this.lockTimer);
      clearTimeout(this.swipeSettle);
    }

    /* ----------------------------------------------------------- pin or not */

    evaluate() {
      const shouldPin = WIDE.matches && !REDUCED.matches && this.panels.length > 1;

      /* Nothing comes "up" in the flow layout, so no panel would ever lift its
         own strip out of its template. Above the carousel's width every panel
         is in normal flow and on screen, so the strips all go in here and
         their own loading="lazy" decides what is fetched; the phone carousel
         keeps its card-by-card reveal. Ahead of the guard below, because the
         first call is usually a no-op transition — flow to flow. */
      if (!shouldPin && (!this.swipeTrack || WIDE.matches)) {
        this.panels.forEach((panel) => this.revealStrip(panel));
      }

      if (shouldPin === this.pinned) {
        if (shouldPin) this.measure();
        return;
      }
      this.pinned = shouldPin;

      if (shouldPin) {
        this.dataset.mode = 'pinned';
        this.style.setProperty('--fe-stage-count', this.panels.length);
        this.measure();
        addEventListener('scroll', this.onScroll, { passive: true });
        if (this.snaps) {
          addEventListener('wheel', this.onWheel, { passive: false });
          addEventListener('keydown', this.onKey);
        }
        this.index = -1;
        this.onScroll();
      } else {
        this.chromeLeft = null;
        this.style.removeProperty('--fe-stage-chrome');
        removeEventListener('scroll', this.onScroll);
        removeEventListener('wheel', this.onWheel);
        removeEventListener('keydown', this.onKey);
        this.removeAttribute('data-mode');
        this.stage.removeAttribute('data-phase');
        this.panels.forEach((p) => {
          p.removeAttribute('data-active');
          p.removeAttribute('inert');
          p.removeAttribute('aria-hidden');
        });
        this.setHex(null);
        this.index = -1;
        this.trackFade();
      }
    }

    /* ------------------------------------------------------ mobile carousel

       At 768px and under the numbered panels become one swipeable strip. The
       CSS owns the scrolling and the snapping — this only moves the track when
       an arrow or a dot is pressed, and reads back which card has settled so
       the dots can say so.

       Two things here are deliberate, because getting either wrong is what
       makes a swipe land between two cards:

       1. Where the strip is now is always read off the layout (scrollLeft and
          offsetLeft), never remembered. A remembered index goes stale the
          moment a finger moves the strip, and the next arrow press then steps
          from the wrong card — the press appears to do nothing, or to jump.
       2. The strip's height is written only when it actually changes, and
          during a swipe it may only grow. Writing it on every scroll frame
          re-lays-out the scroll container mid-snap, which is what leaves the
          strip parked between two cards with the incoming card's copy cut off
          by the clip that height imposes.

       Everything here is inert on the pinned stage: the track is
       display: contents up there, so it has no box to scroll and offsetWidth
       is 0, which is the one check every entry point makes. */

    initSwipe() {
      this.swipeTrack = this.querySelector('[data-swipe-track]');
      if (!this.swipeTrack) return;

      /* the numbered panels only, so a card and a dot always mean each other */
      this.swipeCards = [...this.swipeTrack.querySelectorAll(':scope > [data-role="step"]')];
      this.swipeDots = [...this.querySelectorAll('[data-swipe-goto]')];
      this.swipeIndex = 0;
      this.swipeHeight = '';

      this.querySelector('[data-swipe-prev]')?.addEventListener('click', () => this.slideTo(this.swipeAt() - 1));
      this.querySelector('[data-swipe-next]')?.addEventListener('click', () => this.slideTo(this.swipeAt() + 1));
      this.swipeDots.forEach((dot) => {
        dot.addEventListener('click', () => this.slideTo(Number(dot.dataset.swipeGoto)));
      });

      this.swipeTrack.addEventListener(
        'scroll',
        () => {
          if (this.swipeQueued) return;
          this.swipeQueued = true;
          requestAnimationFrame(() => {
            this.swipeQueued = false;
            /* mid-swipe: the dots keep up, the strip may only grow */
            this.syncSwipe(true);
          });
          /* and once it stops, settle to the card that won */
          this.settleSwipe();
        },
        { passive: true }
      );

      /* scrollend is the real "it has stopped"; the timer is the fallback for
         browsers without it, and is harmless where both run */
      if ('onscrollend' in this.swipeTrack) {
        this.swipeTrack.addEventListener('scrollend', () => this.syncSwipe(), { passive: true });
      }

      requestAnimationFrame(() => this.syncSwipe());
    }

    settleSwipe() {
      clearTimeout(this.swipeSettle);
      this.swipeSettle = setTimeout(() => this.syncSwipe(), 140);
    }

    /* Which card is under the middle of the strip, right now, straight off the
       layout. offsetLeft is measured from the same origin as scrollLeft and,
       unlike a bounding rect, does not shift while a smooth scroll is still in
       flight — so an arrow pressed mid-glide still steps from the right card. */
    swipeAt() {
      const track = this.swipeTrack;
      if (!this.swipeCards?.length || !track.offsetWidth) return 0;
      const middle = track.scrollLeft + track.clientWidth / 2;
      let index = 0;
      let closest = Infinity;
      this.swipeCards.forEach((card, i) => {
        const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle);
        if (distance < closest) {
          closest = distance;
          index = i;
        }
      });
      return index;
    }

    /* The strip loops: past the last card is the first one again. A wrap is a
       jump rather than a glide, because gliding it would rewind the whole strip
       under the reader's thumb. */
    slideTo(index) {
      const count = this.swipeCards?.length || 0;
      if (!count || !this.swipeTrack.offsetWidth) return;

      const next = ((index % count) + count) % count;
      const card = this.swipeCards[next];
      const wrapped = Math.abs(next - this.swipeAt()) > 1;

      /* a whole number, so the strip cannot come to rest half a pixel off a
         snap point and leave a hairline of the neighbour showing */
      const left = Math.round(card.offsetLeft - (this.swipeTrack.clientWidth - card.offsetWidth) / 2);

      /* the dots and the strip's height follow the card being travelled to,
         not the one being left, so nothing is clipped on the way in */
      this.markSwipe(next);
      this.fitSwipe(next, true);

      this.swipeTrack.scrollTo({
        left,
        behavior: wrapped || REDUCED.matches ? 'auto' : 'smooth',
      });
    }

    /* whichever card sits closest to the middle of the track is the one showing */
    syncSwipe(moving) {
      if (!this.swipeCards?.length || !this.swipeTrack.offsetWidth) return;
      const index = this.swipeAt();
      /* called unconditionally, so a resize or a webfont remeasures too */
      this.fitSwipe(index, moving);
      this.markSwipe(index);
    }

    markSwipe(index) {
      this.revealStripsFrom(this.swipeCards, index);
      if (index === this.swipeIndex) return;
      this.swipeIndex = index;
      this.swipeDots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }

    /* The strip stands as tall as the card showing and no taller. The cards
       keep their own heights — the CSS stops them stretching to match — so
       without this the track would still reserve room for the longest panel
       and a short one would sit over a lake of empty card.

       `grow` is the mid-swipe rule: a card coming in may make the strip taller,
       never shorter, so the copy of whichever card is on screen is never cut
       off by a height that belongs to the other one. It shrinks back when the
       swipe settles. Writing only on a change keeps the scroll container from
       being re-laid-out on every frame of a scroll, which is what used to
       disturb the snap. */
    fitSwipe(index, grow) {
      const card = this.swipeCards[index];
      if (!card) return;
      let height = card.offsetHeight;
      if (grow) height = Math.max(height, parseFloat(this.swipeTrack.style.height) || 0);
      const next = `${Math.ceil(height)}px`;
      if (next === this.swipeHeight) return;
      this.swipeHeight = next;
      this.swipeTrack.style.height = next;
    }

    /* After a rotation or a resize the card that was showing must still be the
       one centred: the snap points have all moved. */
    recentreSwipe() {
      if (!this.swipeCards?.length || !this.swipeTrack.offsetWidth) return;
      const card = this.swipeCards[this.swipeIndex] || this.swipeCards[0];
      this.swipeTrack.scrollTo({
        left: Math.round(card.offsetLeft - (this.swipeTrack.clientWidth - card.offsetWidth) / 2),
        behavior: 'auto',
      });
      this.swipeHeight = '';
      this.fitSwipe(this.swipeIndex);
    }

    get snaps() {
      return (this.dataset.advance || 'snap') === 'snap';
    }

    /* is the stage actually stuck to the top of the viewport right now? */
    get held() {
      const box = this.scroller.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(this).getPropertyValue('--fe-stage-top')) || 0;
      return box.top <= top + 2 && box.bottom >= innerHeight - 2;
    }

    /* --------------------------------------------------- one gesture, one panel */

    onWheel(event) {
      if (!this.pinned || !this.snaps || event.ctrlKey) return;
      const dir = event.deltaY > 0 ? 1 : event.deltaY < 0 ? -1 : 0;
      if (!dir || !this.held) return;

      const next = this.index + dir;
      /* at either end, hand the scroll back to the page */
      if (next < 0 || next > this.panels.length - 1) return;

      event.preventDefault();
      if (this.lock || Math.abs(event.deltaY) < 6) return;
      this.step(next);
    }

    onKey(event) {
      if (!this.pinned || !this.snaps || !this.held) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || event.target?.isContentEditable) return;

      const last = this.panels.length - 1;
      let next = null;
      switch (event.key) {
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          next = this.index + 1;
          break;
        case 'ArrowUp':
        case 'PageUp':
          next = this.index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      if (next === null || next < 0 || next > last) return;
      event.preventDefault();
      if (this.lock) return;
      this.step(next);
    }

    /* hold the gesture shut long enough that trackpad momentum from one flick
       cannot count as a second one */
    step(index) {
      this.lock = true;
      clearTimeout(this.lockTimer);
      this.lockTimer = setTimeout(() => {
        this.lock = false;
      }, 650);
      this.goTo(index);
    }

    /* Work out the flight: how far, and how much smaller, the travelling mark
       has to go to land exactly on the closing panel's mark slot.

       Both ends are measured with animation frozen and the target panel forced
       into its active layout, so the numbers describe where things settle
       rather than wherever a transition happens to be mid-flight. */
    locateFlight() {
      if (!this.pinned || !this.flier || !this.landing) return;

      const panel = this.landing.closest('.fe-stack__panel');
      const wasActive = panel.hasAttribute('data-active');
      const wasState = this.flier.dataset.state;
      const wasPhase = this.stage.dataset.phase;
      /* reading a layout property forces the style recalc we need between steps */
      const flush = () => void this.offsetHeight;

      /* 1. freeze, force the settled layout, read both ends */
      this.classList.add('is-locating');
      panel.setAttribute('data-active', '');
      this.flier.dataset.state = 'home';
      this.stage.dataset.phase = 'steps';

      const from = this.flier.getBoundingClientRect();
      const to = this.landing.getBoundingClientRect();

      /* 2. put everything back while still frozen, and let the browser see it —
            otherwise the forced layout becomes the "before" state of the reveal
            we are about to start, and the panel would arrive with no animation */
      if (!wasActive) panel.removeAttribute('data-active');
      this.flier.dataset.state = wasState;
      if (wasPhase) this.stage.dataset.phase = wasPhase;
      flush();

      /* 3. unfreeze, and settle again before anyone starts a transition */
      this.classList.remove('is-locating');
      flush();

      if (!from.height || !to.height) return;
      this.flier.style.setProperty('--fly-x', `${Math.round(to.left + to.width / 2 - (from.left + from.width / 2))}px`);
      this.flier.style.setProperty('--fly-y', `${Math.round(to.top + to.height / 2 - (from.top + from.height / 2))}px`);
      this.flier.style.setProperty('--fly-s', (to.height / from.height).toFixed(4));
    }

    /* size the deck to the tallest panel, so swapping never reflows */
    measure() {
      if (!this.pinned) return;
      /* a remeasure moves the join, and the field is holding the old one */
      if (this.told === 'dark') this.tellField('dark', true);
      this.deck.classList.add('is-measuring');
      let tallest = 0;
      for (const panel of this.panels) tallest = Math.max(tallest, panel.offsetHeight);
      this.deck.classList.remove('is-measuring');
      if (tallest) this.style.setProperty('--fe-deck-h', `${Math.ceil(tallest)}px`);
      this.measureChrome();
      this.trackChrome();
      this.locateFlight();
    }

    /* ------------------------------------------------------- the page chrome
       The announcement bar and the header sit above the hero, so at the top of
       the page the stage's own box runs from under them to a screen's height
       below — and copy centred on that box lands half the chrome's height too
       low. The remedy is to centre on what is actually on screen: the stage
       keeps its full height (so the lattice still reaches every edge) and
       reserves the chrome's leftover height at its foot instead. The reserve
       shrinks one-for-one with scroll, so from the first screen to the moment
       the stage pins itself the copy sits dead centre of the free band. */

    measureChrome() {
      const group = document.querySelectorAll('.shopify-section-group-header-group');
      let height = 0;
      for (const section of group) height += section.offsetHeight;

      /* No header group to read — a standalone page, or a theme that names its
         chrome something else. Fall back to whatever stands above the section,
         which is the same distance whenever the hero leads the page. */
      if (!height) height = Math.max(0, this.getBoundingClientRect().top + scrollY);

      /* A hero that is not the first section has a whole page above it, not a
         strip of chrome; past a third of the screen the number is not chrome. */
      this.chrome = height > innerHeight / 3 ? 0 : height;
    }

    trackChrome() {
      if (!this.pinned) return;
      const left = Math.max(0, (this.chrome || 0) - scrollY);
      if (left === this.chromeLeft) return;
      this.chromeLeft = left;
      this.style.setProperty('--fe-stage-chrome', `${left}px`);
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.evaluate();
        this.recentreSwipe();
        this.syncSwipe();
        this.trackFade();
        if (this.pinned) {
          this.measure();
          this.index = -1;
          this.onScroll();
        }
      }, 150);
    }

    /* -------------------------------------------------------------- scroll */

    /* a scroll up while the stage is held is a step back through the panels,
       not a reason to bring the header down over them */
    holdHeader() {
      const y = scrollY;
      const up = y < this.lastY;
      this.lastY = y;
      if (!up || !this.pinned || !this.held) return;
      this.header.dispatchEvent(new Event('preventHeaderReveal'));
    }

    onScroll() {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.trackFade();
        if (!this.pinned) return;
        this.trackChrome();
        if (this.visible || this.index < 0) this.update();
      });
    }

    get metrics() {
      const box = this.scroller.getBoundingClientRect();
      const stageH = this.stage.offsetHeight;
      const usable = Math.max(this.scroller.offsetHeight - stageH, 1);
      return { travelled: -box.top, usable, top: box.top + scrollY };
    }

    update() {
      const { travelled, usable } = this.metrics;
      const count = this.panels.length;
      const p = Math.min(Math.max(travelled / usable, 0), 1);
      const next = Math.min(count - 1, Math.floor(p * count));
      if (next === this.index) return;

      this.dataset.dir = next > this.index ? 'down' : 'up';
      this.index = next;
      this.show(next);
    }

    show(index) {
      const active = this.panels[index];
      const flying = !!this.flier && active.dataset.tone === 'dark';

      /* Measure before anything starts moving: locating freezes transitions,
         so doing it after activation would cancel the reveal we just began. */
      if (flying) this.locateFlight();

      this.panels.forEach((panel, i) => {
        const on = i === index;
        panel.toggleAttribute('data-active', on);
        panel.toggleAttribute('inert', !on);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      this.revealStripsFrom(this.panels, index);

      this.stage.dataset.phase = active.dataset.role === 'hero' ? 'hero' : 'steps';
      this.setHex(active.dataset.hex || null);
      this.setTone(active.dataset.tone === 'dark' ? 'dark' : 'light');

      if (this.flier) this.flier.dataset.state = flying ? 'flown' : 'home';

      this.railBtns.forEach((btn, i) => btn.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }

    /* The closing panel covers the lattice with a solid ground, so park the
       lattice while it is hidden rather than painting behind an opaque layer. */
    setTone(tone) {
      if (this.stage.dataset.tone === tone) return;
      this.stage.dataset.tone = tone;
      for (const lattice of this.lattices) lattice.toggleAttribute('data-paused', tone === 'dark');
      this.tellField(tone);
    }

    /* The page's ground cannot work this boundary out for itself. Every other
       one is a section that is dark for its whole height, so its edges are the
       boundary; here the stage is one screen that is light for most of its
       scroll and teal at the very end, and where that happens is the hero's
       business rather than anything readable off the layout.

       So the hero says so. The field holds whatever it was last told for as
       long as the hero is on screen, and goes back to reading its sections
       when the hero leaves — which is the hand-off into the section below,
       and the transition that hand-off is supposed to make. */
    tellField(tone, force) {
      if (this.told === tone && !force) return;
      this.told = tone;
      /* Where the claim runs out: the bottom of the stage's scroll, which is
         the join with whatever follows. The field releases it on the same line
         it uses for every other boundary, so this hand-off happens where a
         reader expects it — as the join comes up the screen — rather than once
         the stage has already gone, which is late enough to read as the colour
         being stuck. */
      const until = tone === 'dark' && this.pinned && this.scroller
        ? Math.round(this.metrics.top + this.scroller.offsetHeight)
        : null;
      this.dispatchEvent(
        new CustomEvent('fe-field:tone', { bubbles: true, detail: { tone, until } })
      );
    }

    /* ------------------------------------------------------- the logo strips
       Each panel's animated strip waits in a <template>, which the browser
       parses but never loads. Every panel is inside the pinned stage at once,
       hidden only by opacity, so an image in any of them counts as on screen
       and all of them would download together — several megabytes, in the
       hero, competing with everything that matters for first paint.

       A strip is lifted out when its panel comes up, and the next one with it
       so it is ready before it is asked for. The copy keeps loading="lazy",
       so a strip lifted out while still off screen — the card after this one
       in the phone carousel — waits until it is scrolled to. */

    revealStrip(panel) {
      const holder = panel?.querySelector?.('[data-strip]');
      if (!holder || 'stripShown' in holder.dataset) return;
      const template = holder.querySelector('template[data-strip-img]');
      if (!template) return;
      holder.dataset.stripShown = '';
      holder.append(template.content.cloneNode(true));
    }

    revealStripsFrom(list, index) {
      if (!list?.length) return;
      this.revealStrip(list[index]);
      this.revealStrip(list[index + 1]);
    }

    setHex(slug) {
      for (const hex of this.hexes) {
        hex.dataset.state = slug && hex.dataset.hex === slug ? 'active' : 'idle';
      }
    }

    /* ------------------------------------------------------ the ground's fade
       Over the honeycomb the ground goes from light to teal along the run of
       panels, and the copy's colours go with it. The fade always finishes as
       the final panel arrives; data-fade says where it starts — "all" the
       moment the stage sticks, a number for the last so many panels, "none"
       to stay light.

       Pinned, the scroller's travel is the clock: panel i owns the band
       [i, i+1) of it, and a panel is "arrived" at the centre of its band,
       which is where goTo() puts it. So the fade runs from the top of the
       travel ("all") or the centre of the first panel it spans, to the centre
       of the last.

       The blend is the pinned stage's effect. In the flow layout — phones,
       reduced motion, no stage to stick — the panels are a plain stack, so
       the honeycomb is held on its light look and nothing fades. */

    /* "window" leaves the pacing to the honeycomb's own page-scroll range
       while the stage is pinned; the panel-paced values are fed from here */
    get drivesFade() {
      return (this.dataset.fade || 'all') !== 'window';
    }

    get fadePanels() {
      const span = this.dataset.fade || 'all';
      const whole = this.panels.length - 1;
      if (span === 'none' || span === 'window') return 0;
      if (span === 'all') return whole;
      return Math.min(whole, Math.max(0, parseInt(span, 10) || 0));
    }

    fadeProgress() {
      const k = this.fadePanels;
      if (k <= 0 || !this.pinned) return 0;
      const clamp = (v) => Math.min(Math.max(v, 0), 1);
      const count = this.panels.length;
      const { travelled, usable } = this.metrics;
      const at = clamp(travelled / usable) * count;
      const from = this.dataset.fade === 'all' ? 0 : count - 1 - k + 0.5;
      return clamp((at - from) / (count - 0.5 - from));
    }

    /* Who is feeding the honeycomb right now: nobody in the flow layout (it
       is parked on the light look), the honeycomb's own page-scroll reading
       for "window", this element for the panel-paced spans. */
    trackFade() {
      if (!this.honeycomb) return;
      const mode = !this.pinned ? 'still' : this.drivesFade ? 'panels' : 'window';
      if (mode !== this.fadeMode) {
        this.fadeMode = mode;
        this.honeycomb.useWindowScroll?.(mode === 'window');
        if (mode === 'still') {
          this.fade = 0;
          this.honeycomb.setProgress?.(0);
        }
      }
      if (mode !== 'panels') return;
      const fade = this.fadeProgress();
      if (fade === this.fade) return;
      this.fade = fade;
      this.honeycomb.setProgress?.(fade);
    }

    goTo(index, behavior) {
      if (Number.isNaN(index) || index < 0) return;
      if (!this.pinned) {
        this.panels[index]?.scrollIntoView({ behavior: behavior || 'smooth', block: 'center' });
        return;
      }
      const { usable, top } = this.metrics;
      const target = top + ((index + 0.5) / this.panels.length) * usable;
      scrollTo({
        top: target,
        behavior: behavior || (REDUCED.matches ? 'auto' : 'smooth'),
      });
    }
  }

  if (!customElements.get('fe-stacked-hero')) customElements.define('fe-stacked-hero', FeStackedHero);
})();
